import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, SOURCES, SOURCE_LABEL, formatDateIST } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLookups } from "../lib/lookups";
import DateRangePicker from "../components/DateRangePicker";
import { Upload, FileText, AlertCircle, Check, RefreshCw, Save, Cloud, Download, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const INTERNAL_FIELDS = [
  "lead_id", "created_at", "name", "email", "phone", "platform", "is_organic",
  "campaign_id", "campaign_name", "adset_id", "adset_name", "ad_id", "ad_name",
  "form_id", "form_name", "inbox_url", "source_status", "course_interest",
  "education_level", "career_goal", "keyword", "source_channel", "referred_by",
  "budget", "notes",
];

export default function Import() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  useEffect(() => { if (user && !isAdmin) navigate("/", { replace: true }); }, [user, isAdmin, navigate]);
  const { CATEGORIES, STAGES, COURSES } = useLookups();
  const [source, setSource] = useState("meta-fb");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [issues, setIssues] = useState([]);
  const [committing, setCommitting] = useState(false);
  const [issuesRange, setIssuesRange] = useState({ from: null, to: null });

  // Google Sheets Auto-Sync — multi-URL per source
  const SYNC_SOURCES_META = ["meta", "google", "offline", "whatsapp"];
  const emptyUrls = () => ({ meta: [], google: [], offline: [], whatsapp: [] });
  const [syncUrls, setSyncUrls] = useState(emptyUrls());
  const [syncStatus, setSyncStatus] = useState({});
  const [syncing, setSyncing] = useState({});
  const [savingUrls, setSavingUrls] = useState(false);

  const addUrl = (kind) =>
    setSyncUrls((p) => ({ ...p, [kind]: [...p[kind], { url: "", label: "" }] }));
  const removeUrl = (kind, idx) =>
    setSyncUrls((p) => ({ ...p, [kind]: p[kind].filter((_, i) => i !== idx) }));
  const updateUrl = (kind, idx, field, val) =>
    setSyncUrls((p) => ({
      ...p,
      [kind]: p[kind].map((u, i) => (i === idx ? { ...u, [field]: val } : u)),
    }));

  // Export
  const [exporting, setExporting] = useState(false);
  const [expFilters, setExpFilters] = useState({ category: "", stage: "", course: "", source: "" });
  const [expRange, setExpRange] = useState({ from: null, to: null });

  const triggerDownload = async (params) => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params).toString();
      const resp = await fetch(`${API}/export/leads${qs ? `?${qs}` : ""}`, {
        credentials: "include",
      });
      if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
      const blob = await resp.blob();
      const disp = resp.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `leads_export_${Date.now()}.csv`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const exportAll = () => triggerDownload({});
  const exportFiltered = () => {
    const params = {};
    Object.entries(expFilters).forEach(([k, v]) => v && (params[k] = v));
    if (expRange.from) params.date_from = expRange.from;
    if (expRange.to) params.date_to = expRange.to;
    triggerDownload(params);
  };

  const hasFilter = !!(expFilters.category || expFilters.stage || expFilters.course || expFilters.source || expRange.from || expRange.to);

  const loadSync = useCallback(async () => {
    if (!isAdmin) return;
    const { data } = await api.get("/sync/status");
    setSyncStatus(data.status || {});
    setSyncUrls({
      meta: data.sheet_sync_meta_urls || [],
      google: data.sheet_sync_google_urls || [],
      offline: data.sheet_sync_offline_urls || [],
      whatsapp: data.sheet_sync_whatsapp_urls || [],
    });
  }, [isAdmin]);
  useEffect(() => { loadSync(); }, [loadSync]);

  const saveUrls = async () => {
    setSavingUrls(true);
    try {
      await api.put("/settings", {
        sheet_sync_meta_urls: syncUrls.meta.filter((u) => u.url.trim()),
        sheet_sync_google_urls: syncUrls.google.filter((u) => u.url.trim()),
        sheet_sync_offline_urls: syncUrls.offline.filter((u) => u.url.trim()),
        sheet_sync_whatsapp_urls: syncUrls.whatsapp.filter((u) => u.url.trim()),
      });
      toast.success("Sheet URLs saved");
      loadSync();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSavingUrls(false);
    }
  };

  const syncNow = async (kind) => {
    setSyncing((p) => ({ ...p, [kind]: true }));
    try {
      const { data } = await api.post(`/sync/run?source_kind=${kind}`);
      if (data.error) toast.error(`${kind}: ${data.error}`);
      else if (data.skipped_reason === "no_url") toast.error(`${kind}: add a URL first`);
      else if (data.skipped_reason === "already_running") toast.info(`${kind}: sync already in progress`);
      else {
        const urlCount = (data.url_results || []).length;
        toast.success(
          `${kind}${urlCount > 1 ? ` (${urlCount} sheets)` : ""}: +${data.inserted} new · ${data.skipped} skipped · ${data.already_synced || 0} already synced`
        );
      }
      loadSync();
      loadIssues();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing((p) => ({ ...p, [kind]: false }));
    }
  };

  const loadIssues = useCallback(async () => {
    if (!isAdmin) return;
    const params = {};
    if (issuesRange.from) params.date_from = issuesRange.from;
    if (issuesRange.to) params.date_to = issuesRange.to;
    const { data } = await api.get("/import/issues", { params });
    setIssues(data);
  }, [isAdmin, issuesRange]);
  useEffect(() => { loadIssues(); }, [loadIssues]);

  const uploadPreview = async () => {
    if (!file) return toast.error("Pick a CSV file");
    const fd = new FormData();
    fd.append("source", source);
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/import/commit", { source, rows: preview.rows, mapping });
      toast.success(`Imported: ${data.inserted} new, ${data.updated} updated, ${data.skipped} skipped, ${data.issues_count} issues`);
      setPreview(null);
      setFile(null);
      setMapping({});
      loadIssues();
    } catch (e) {
      toast.error("Import failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-[540]">Import Leads</h1>

      <div className="bg-white border border-[#E8EAEF] rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.08)] space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-lg font-[540] flex items-center gap-2"><Cloud className="h-5 w-5 text-[#6C47FF]" /> Google Sheets Auto-Sync</h2>
          <span className="text-xs text-[#6B7280]">Runs every 15 min · dedupes by phone number</span>
        </div>
        <p className="text-xs text-[#6B7280]">In Google Sheets: File → Share → Publish to web → choose the sheet tab → format <strong>CSV</strong> → copy the link. Add one URL per sheet/form below.</p>
        {[
          { kind: "meta", label: "Meta (FB + IG)" },
          { kind: "google", label: "Google Ads" },
          { kind: "whatsapp", label: "WhatsApp" },
          { kind: "offline", label: "Offline" },
        ].map(({ kind, label }) => {
          const status = syncStatus[kind] || {};
          const urls = syncUrls[kind] || [];
          const hasUrl = urls.some((u) => u.url.trim());
          return (
            <div key={kind} className="border border-[#E8EAEF] rounded-[8px] p-4 bg-[#F0F1F7] space-y-3" data-testid={`sync-row-${kind}`}>
              {/* Header row */}
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-[600]">{label}</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-[11px] text-[#6B7280]" data-testid={`sync-summary-${kind}`}>
                    {status.last_sync_at ? (
                      <>Last synced: <span className="font-[600] text-[#1A1A2E]">{formatDateIST(status.last_sync_at)}</span></>
                    ) : "Never synced"}
                    {status.last_result && (
                      <> · <span>+{status.last_result.inserted} new · {status.last_result.skipped} skipped</span></>
                    )}
                    {status.last_error && <span className="text-[#7f1d1d] ml-1">{status.last_error}</span>}
                  </div>
                  <button
                    onClick={() => syncNow(kind)}
                    disabled={!!syncing[kind] || !hasUrl}
                    data-testid={`sync-now-${kind}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] border border-[#6C47FF] bg-white text-xs font-[600] disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${syncing[kind] ? "animate-spin" : ""}`} />
                    {syncing[kind] ? "Syncing…" : "Sync Now"}
                  </button>
                </div>
              </div>

              {/* URL list */}
              <div className="space-y-2">
                {urls.map((entry, idx) => (
                  <div key={idx} className="flex gap-2 items-center flex-wrap" data-testid={`sync-url-row-${kind}-${idx}`}>
                    <input
                      type="text"
                      value={entry.label}
                      onChange={(e) => updateUrl(kind, idx, "label", e.target.value)}
                      placeholder={`Label (e.g. Form ${idx + 1})`}
                      className="w-[160px] rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[40px] text-sm bg-white"
                    />
                    <input
                      type="url"
                      value={entry.url}
                      onChange={(e) => updateUrl(kind, idx, "url", e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/d/.../pub?output=csv"
                      data-testid={`sync-url-${kind}-${idx}`}
                      className="flex-1 min-w-[220px] rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[40px] text-sm bg-white"
                    />
                    <button
                      onClick={() => removeUrl(kind, idx)}
                      className="text-[#7f1d1d] hover:opacity-70 p-1"
                      title="Remove this URL"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addUrl(kind)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] border border-dashed border-[#6C47FF] text-xs font-[600] text-[#6C47FF] hover:bg-white"
                >
                  <Plus className="h-3.5 w-3.5" /> Add {urls.length === 0 ? "Sheet URL" : "Another Sheet"}
                </button>
              </div>
            </div>
          );
        })}
        <div className="flex justify-end">
          <button
            onClick={saveUrls}
            disabled={savingUrls}
            data-testid="sync-save-urls"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] btn-mx text-sm font-[600] min-h-[44px] disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> {savingUrls ? "Saving…" : "Save URLs"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E8EAEF] rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.08)] space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-lg font-[540] flex items-center gap-2"><Download className="h-5 w-5 text-[#6C47FF]" /> Export Leads</h2>
          <button
            onClick={exportAll}
            disabled={exporting}
            data-testid="export-all-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] btn-mx text-sm font-[600] min-h-[44px] disabled:opacity-60"
          >
            <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export All Leads as CSV"}
          </button>
        </div>
        <div className="border-t border-[#E8EAEF] pt-4">
          <div className="text-xs font-[600] text-[#6B7280] uppercase tracking-wider mb-3">Filtered Export</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <select value={expFilters.category} onChange={(e) => setExpFilters({ ...expFilters, category: e.target.value })} data-testid="export-filter-category" className="rounded-[6px] border border-[#E8EAEF] px-2 py-2 min-h-[44px] text-sm bg-white">
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={expFilters.stage} onChange={(e) => setExpFilters({ ...expFilters, stage: e.target.value })} data-testid="export-filter-stage" className="rounded-[6px] border border-[#E8EAEF] px-2 py-2 min-h-[44px] text-sm bg-white">
              <option value="">All Stages</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={expFilters.course} onChange={(e) => setExpFilters({ ...expFilters, course: e.target.value })} data-testid="export-filter-course" className="rounded-[6px] border border-[#E8EAEF] px-2 py-2 min-h-[44px] text-sm bg-white">
              <option value="">All Courses</option>
              {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={expFilters.source} onChange={(e) => setExpFilters({ ...expFilters, source: e.target.value })} data-testid="export-filter-source" className="rounded-[6px] border border-[#E8EAEF] px-2 py-2 min-h-[44px] text-sm bg-white">
              <option value="">All Sources</option>
              {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <DateRangePicker value={expRange} onChange={setExpRange} testid="export-range" />
            <button
              onClick={exportFiltered}
              disabled={exporting || !hasFilter}
              data-testid="export-filtered-btn"
              title={hasFilter ? "" : "Pick at least one filter"}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] border border-[#6C47FF] bg-white text-sm font-[600] min-h-[44px] disabled:opacity-50"
            >
              <Download className="h-4 w-4" /> Export Filtered as CSV
            </button>
          </div>
          <p className="text-xs text-[#6B7280] mt-2">Includes all lead fields plus Category, Stage, Summary/Notes, and Next Reminder Date. Dates exported in IST.</p>
        </div>
      </div>

      <div className="bg-white border border-[#E8EAEF] rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.08)] space-y-4">
        <h2 className="text-lg font-[540]">CSV Upload</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[#6B7280]">Source</label>
            <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="import-source" className="w-full mt-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white">
              {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-[#6B7280]">CSV File</label>
            <div className="flex gap-2 mt-1">
              <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file" className="flex-1 rounded-[6px] border border-[#E8EAEF] px-3 py-2 min-h-[44px] text-sm bg-white" />
              <button onClick={uploadPreview} data-testid="import-preview-btn" className="btn-mx rounded-[8px] px-4 py-2 text-sm font-[600] min-h-[44px] inline-flex items-center gap-2">
                <Upload className="h-4 w-4" /> Preview
              </button>
            </div>
          </div>
        </div>
      </div>

      {preview && (
        <div className="bg-white border border-[#E8EAEF] rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.08)] space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-[540]">Confirm Column Mapping</h2>
            <span className="text-sm text-[#6B7280]">{preview.total_rows} rows detected</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F0F1F7] border-b border-[#E8EAEF]">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-[600] text-[#6B7280] uppercase">CSV Column</th>
                  <th className="text-left px-3 py-2 text-xs font-[600] text-[#6B7280] uppercase">Maps to</th>
                  <th className="text-left px-3 py-2 text-xs font-[600] text-[#6B7280] uppercase">Sample</th>
                </tr>
              </thead>
              <tbody>
                {preview.headers.map((h) => (
                  <tr key={h} className="border-b border-[#E8EAEF]">
                    <td className="px-3 py-2 font-[600] text-[#1A1A2E]">{h}</td>
                    <td className="px-3 py-2">
                      <select
                        value={mapping[h] || ""}
                        onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                        data-testid={`mapping-${h}`}
                        className="rounded-[6px] border border-[#E8EAEF] px-2 py-1.5 text-xs bg-white"
                      >
                        <option value="">— Skip —</option>
                        {INTERNAL_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-[#6B7280] text-xs">{preview.sample_rows[0]?.[h] || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setPreview(null)} className="px-4 py-2 rounded-[8px] border border-[#6C47FF] text-sm font-[600] min-h-[44px]">Cancel</button>
            <button onClick={commit} disabled={committing} data-testid="import-commit-btn" className="btn-mx rounded-[8px] px-4 py-2 text-sm font-[600] min-h-[44px] inline-flex items-center gap-2 disabled:opacity-60">
              <Check className="h-4 w-4" /> {committing ? "Importing..." : "Confirm & Import"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E8EAEF] rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-[540] flex items-center gap-2"><AlertCircle className="h-5 w-5 text-amber-700" /> Import Issues</h2>
          <div className="flex items-center gap-2">
            <DateRangePicker value={issuesRange} onChange={setIssuesRange} testid="import-issues-range" />
            {issues.length > 0 && (
              <button onClick={async () => { await api.delete("/import/issues"); loadIssues(); }} data-testid="clear-issues" className="text-xs px-3 py-1.5 rounded-[8px] border border-[#6C47FF] font-[600]">Clear all</button>
            )}
          </div>
        </div>
        {issues.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No issues. Imports are clean.</div>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {issues.map((i, idx) => (
              <div key={idx} className="text-xs border border-[#E8EAEF] rounded-[8px] p-3 bg-[#F0F1F7]" data-testid={`issue-row-${idx}`}>
                <div className="font-[600] text-amber-800">{i.error}</div>
                <div className="text-[#6B7280] mt-1">Source: {i.source} · Row {i.row_index + 1}</div>
                <div className="text-[#1A1A2E] mt-1 truncate">{JSON.stringify(i.raw)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#F0F1F7] border border-[#E8EAEF] rounded-[12px] p-6 text-sm text-[#6B7280]">
        <div className="flex items-center gap-2 mb-2 text-[#1A1A2E] font-[600]"><FileText className="h-4 w-4" /> Expected CSV columns</div>
        <p><strong>Meta:</strong> id, created_time, ad_id, ad_name, adset_id, adset_name, campaign_id, campaign_name, form_id, form_name, platform, is_organic, full name, email, phone_number, inbox_url, lead_status, which_course_you_are_looking_for?, what_is_your_current_education_level?, what_is_your_career_goal?</p>
        <p className="mt-2"><strong>Google:</strong> id, created_time, campaign_name, adset_name, ad_name, keyword, platform, is_organic, source, full name, email, phone_number, course_interest, budget, notes</p>
        <p className="mt-2"><strong>Offline:</strong> id, created_time, platform, is_organic, source, referred_by, full name, email, phone_number, course_interest, education_level, career_goal, budget, notes</p>
      </div>
    </div>
  );
}
